import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FileText, Search, Filter, X, Calendar, DollarSign,
  Building2, ChevronLeft, ChevronRight, ArrowUpDown,
  Paperclip, Receipt, BookOpen, RefreshCw, FolderOpen,
  ArrowDown, ArrowUp, Download, BarChart3, TrendingUp,
  TrendingDown, AlertTriangle, Clock
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer
} from 'recharts';

const API = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

// ── Document type configuration ──
const DOC_TYPE_CONFIG = {
  Invoice:      { icon: FileText,  color: 'bg-blue-100 text-blue-700 border-blue-200',    dot: 'bg-blue-500',   path: '/invoices' },
  Expense:      { icon: Receipt,   color: 'bg-purple-100 text-purple-700 border-purple-200', dot: 'bg-purple-500', path: '/expenses' },
  Accrual:      { icon: BookOpen,  color: 'bg-amber-100 text-amber-700 border-amber-200',  dot: 'bg-amber-500',  path: '/accruals' },
  Subscription: { icon: RefreshCw, color: 'bg-green-100 text-green-700 border-green-200',  dot: 'bg-green-500',  path: '/subscriptions' },
};

// ── Status badge colours ──
const STATUS_COLORS = {
  Draft:              'bg-gray-100 text-gray-600 border-gray-200',
  Pending:            'bg-yellow-100 text-yellow-700 border-yellow-200',
  'Pending Approval': 'bg-yellow-100 text-yellow-700 border-yellow-200',
  Submitted:          'bg-blue-100 text-blue-700 border-blue-200',
  Approved:           'bg-green-100 text-green-700 border-green-200',
  Rejected:           'bg-red-100 text-red-600 border-red-200',
  Posted:             'bg-emerald-100 text-emerald-700 border-emerald-200',

  Active:             'bg-blue-100 text-blue-700 border-blue-200',
  Matched:            'bg-amber-100 text-amber-700 border-amber-200',
  Settled:            'bg-green-100 text-green-700 border-green-200',
  Cancelled:          'bg-gray-100 text-gray-500 border-gray-200',
  Expired:            'bg-red-100 text-red-600 border-red-200',
  Ready:              'bg-teal-100 text-teal-700 border-teal-200',
};

function formatCurrency(amount, currency = 'GBP') {
  if (amount == null) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount);
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ═══════════════════════════════════════════════════════════════════════════
// CHART HELPERS
// ═══════════════════════════════════════════════════════════════════════════
const CHART_COLORS = {
  Invoice: '#3b82f6',
  Expense: '#a855f7',
  Accrual: '#f59e0b',
  Subscription: '#22c55e',
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold text-gray-900 mb-1">{label}</p>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-gray-600">{entry.name}:</span>
          <span className="font-medium">{formatCurrency(entry.value)}</span>
        </div>
      ))}
      {payload.length > 1 && (
        <div className="border-t border-gray-100 mt-1 pt-1 font-semibold text-gray-900">
          Total: {formatCurrency(payload.reduce((s, e) => s + (e.value || 0), 0))}
        </div>
      )}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════════════
// ANALYTICS VIEW
// ═══════════════════════════════════════════════════════════════════════════
function AnalyticsView({ data, loading, financialYears, selectedFY, onFYChange }) {
  const [chartView, setChartView] = useState(null);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-400">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-3" />
          Loading analytics...
        </div>
      </div>
    );
  }

  const { fyLabel, postedByMonth, dueByMonth, summary } = data;

  const totalPostedAll = postedByMonth.reduce((s, m) => s + m.total, 0);
  const totalDueAll = dueByMonth.reduce((s, m) => s + m.total, 0);

  // Auto-select view: default to posted if there's posted data, otherwise due
  const effectiveView = chartView ?? (totalPostedAll > 0 ? 'posted' : 'due');
  const chartData = effectiveView === 'posted' ? postedByMonth : dueByMonth;

  return (
    <div className="space-y-6">
      {/* FY Selector */}
      {financialYears && financialYears.length > 0 && (
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-600">Financial Year:</label>
          <select
            value={selectedFY || ''}
            onChange={(e) => onFYChange(e.target.value ? Number(e.target.value) : null)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            {financialYears.map((fy) => (
              <option key={fy.FinancialYear} value={fy.FinancialYear}>
                {fy.YearLabel} {fy.IsCurrent ? '(Current)' : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="bg-emerald-100 p-1.5 rounded-lg">
              <TrendingUp size={16} className="text-emerald-600" />
            </div>
            <span className="text-xs font-medium text-gray-500 uppercase">Total Posted</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary.totalPosted)}</p>
          <p className="text-xs text-gray-500 mt-0.5">FY {fyLabel}</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="bg-blue-100 p-1.5 rounded-lg">
              <Clock size={16} className="text-blue-600" />
            </div>
            <span className="text-xs font-medium text-gray-500 uppercase">Outstanding</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary.totalDue)}</p>
          <p className="text-xs text-gray-500 mt-0.5">Awaiting payment</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="bg-red-100 p-1.5 rounded-lg">
              <AlertTriangle size={16} className="text-red-600" />
            </div>
            <span className="text-xs font-medium text-gray-500 uppercase">Overdue</span>
          </div>
          <p className="text-2xl font-bold text-red-600">{formatCurrency(summary.totalOverdue)}</p>
          <p className="text-xs text-gray-500 mt-0.5">{summary.overdueCount} document{summary.overdueCount !== 1 ? 's' : ''}</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="bg-indigo-100 p-1.5 rounded-lg">
              <BarChart3 size={16} className="text-indigo-600" />
            </div>
            <span className="text-xs font-medium text-gray-500 uppercase">FY Cashflow</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalPostedAll + totalDueAll)}</p>
          <p className="text-xs text-gray-500 mt-0.5">Posted + outstanding</p>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {effectiveView === 'posted' ? 'Payments by Month' : 'Due by Month'}
            </h3>
            <p className="text-sm text-gray-500">FY {fyLabel} — April to March</p>
          </div>
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setChartView('posted')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                effectiveView === 'posted' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Posted
            </button>
            <button
              onClick={() => setChartView('due')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                effectiveView === 'due' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Due / Upcoming
            </button>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={340}>
          <BarChart data={chartData} barCategoryGap="30%" maxBarSize={40}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis
              tick={{ fontSize: 11 }}
              tickFormatter={(v) => v >= 1000 ? `£${(v / 1000).toFixed(0)}k` : `£${v}`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
            />
            <Bar dataKey="Invoice" stackId="a" fill={CHART_COLORS.Invoice} />
            <Bar dataKey="Expense" stackId="a" fill={CHART_COLORS.Expense} />
            <Bar dataKey="Accrual" stackId="a" fill={CHART_COLORS.Accrual} />
            <Bar dataKey="Subscription" stackId="a" fill={CHART_COLORS.Subscription} radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Monthly Breakdown Tables */}
      <div className="grid grid-cols-2 gap-6">
        {/* Posted Table */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 bg-emerald-50 border-b border-emerald-100">
            <h4 className="font-semibold text-emerald-800 flex items-center gap-2">
              <TrendingUp size={16} /> Payments Made
            </h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Month</th>
                  <th className="text-right px-3 py-2 font-medium text-blue-600">Inv</th>
                  <th className="text-right px-3 py-2 font-medium text-purple-600">Exp</th>
                  <th className="text-right px-3 py-2 font-medium text-amber-600">Acc</th>
                  <th className="text-right px-3 py-2 font-medium text-green-600">Sub</th>
                  <th className="text-right px-3 py-2 font-semibold text-gray-900">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {postedByMonth.map((m) => (
                  <tr key={m.monthKey} className={m.total > 0 ? 'hover:bg-gray-50' : 'text-gray-300'}>
                    <td className="px-3 py-2 font-medium text-gray-700">{m.month}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{m.Invoice ? formatCurrency(m.Invoice) : '—'}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{m.Expense ? formatCurrency(m.Expense) : '—'}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{m.Accrual ? formatCurrency(m.Accrual) : '—'}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{m.Subscription ? formatCurrency(m.Subscription) : '—'}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs font-semibold">{m.total ? formatCurrency(m.total) : '—'}</td>
                  </tr>
                ))}
                <tr className="bg-emerald-50 font-semibold">
                  <td className="px-3 py-2 text-emerald-800">Total</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{formatCurrency(postedByMonth.reduce((s, m) => s + m.Invoice, 0))}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{formatCurrency(postedByMonth.reduce((s, m) => s + m.Expense, 0))}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{formatCurrency(postedByMonth.reduce((s, m) => s + m.Accrual, 0))}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{formatCurrency(postedByMonth.reduce((s, m) => s + m.Subscription, 0))}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs text-emerald-700">{formatCurrency(totalPostedAll)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Due Table */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 bg-blue-50 border-b border-blue-100">
            <h4 className="font-semibold text-blue-800 flex items-center gap-2">
              <Clock size={16} /> Due / Upcoming
            </h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Month</th>
                  <th className="text-right px-3 py-2 font-medium text-blue-600">Inv</th>
                  <th className="text-right px-3 py-2 font-medium text-purple-600">Exp</th>
                  <th className="text-right px-3 py-2 font-medium text-amber-600">Acc</th>
                  <th className="text-right px-3 py-2 font-medium text-green-600">Sub</th>
                  <th className="text-right px-3 py-2 font-semibold text-gray-900">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {dueByMonth.map((m) => (
                  <tr key={m.monthKey} className={m.total > 0 ? 'hover:bg-gray-50' : 'text-gray-300'}>
                    <td className="px-3 py-2 font-medium text-gray-700">{m.month}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{m.Invoice ? formatCurrency(m.Invoice) : '—'}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{m.Expense ? formatCurrency(m.Expense) : '—'}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{m.Accrual ? formatCurrency(m.Accrual) : '—'}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{m.Subscription ? formatCurrency(m.Subscription) : '—'}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs font-semibold">{m.total ? formatCurrency(m.total) : '—'}</td>
                  </tr>
                ))}
                <tr className="bg-blue-50 font-semibold">
                  <td className="px-3 py-2 text-blue-800">Total</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{formatCurrency(dueByMonth.reduce((s, m) => s + m.Invoice, 0))}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{formatCurrency(dueByMonth.reduce((s, m) => s + m.Expense, 0))}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{formatCurrency(dueByMonth.reduce((s, m) => s + m.Accrual, 0))}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{formatCurrency(dueByMonth.reduce((s, m) => s + m.Subscription, 0))}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs text-blue-700">{formatCurrency(totalDueAll)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════
export default function AllDocumentsPage() {
  const { token, currentCompanyId } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Tab
  const [activeTab, setActiveTab] = useState('documents');

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);
  const [filteredAmount, setFilteredAmount] = useState(0);
  const [stats, setStats] = useState({});
  const [statuses, setStatuses] = useState([]);

  // Analytics
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedFY, setSelectedFY] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [documentType, setDocumentType] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Sync search param from URL (e.g. from global search navigation)
  useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    if (urlSearch) setSearchTerm(urlSearch);
  }, [searchParams]);

  // Sorting
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);

  const headers = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-company-id': currentCompanyId,
  }), [token, currentCompanyId]);

  // ── Fetch documents ──
  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (documentType) params.append('documentType', documentType);
      if (statusFilter) params.append('status', statusFilter);
      if (searchTerm) params.append('search', searchTerm);
      if (vendorFilter) params.append('vendor', vendorFilter);
      if (departmentFilter) params.append('department', departmentFilter);
      if (dateFrom) params.append('dateFrom', dateFrom);
      if (dateTo) params.append('dateTo', dateTo);
      params.append('sortBy', sortBy);
      params.append('sortDir', sortDir);
      params.append('page', page);
      params.append('pageSize', pageSize);

      const res = await fetch(`${API}/all-documents?${params}`, { headers: headers() });
      if (!res.ok) throw new Error('Failed to fetch documents');
      const data = await res.json();
      setDocuments(data.documents || []);
      setTotal(data.total || 0);
      setFilteredAmount(data.filteredAmount || 0);
      setStats(data.stats || {});
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [headers, documentType, statusFilter, searchTerm, vendorFilter, departmentFilter, dateFrom, dateTo, sortBy, sortDir, page, pageSize]);

  // ── Fetch statuses for filter dropdown ──
  const fetchStatuses = useCallback(async () => {
    try {
      const res = await fetch(`${API}/all-documents/statuses`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setStatuses(data.statuses || []);
      }
    } catch (err) {
      console.error('Fetch statuses error:', err);
    }
  }, [headers]);

  // ── Fetch financial years ──
  const fetchFinancialYears = useCallback(async () => {
    try {
      const res = await fetch(`${API}/all-documents/financial-years`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setFinancialYears(data.financialYears || []);
        // Default to the current FY
        const current = (data.financialYears || []).find(fy => fy.IsCurrent);
        if (current && !selectedFY) setSelectedFY(current.FinancialYear);
      }
    } catch (err) {
      console.error('Fetch FYs error:', err);
    }
  }, [headers, selectedFY]);

  // ── Fetch analytics data ──
  const fetchAnalytics = useCallback(async () => {
    try {
      setAnalyticsLoading(true);
      const params = selectedFY ? `?fy=${selectedFY}` : '';
      const res = await fetch(`${API}/all-documents/analytics${params}`, { headers: headers() });
      if (!res.ok) throw new Error('Failed to fetch analytics');
      const data = await res.json();
      setAnalyticsData(data);
    } catch (err) {
      console.error('Fetch analytics error:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [headers, selectedFY]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);
  useEffect(() => { fetchStatuses(); }, [fetchStatuses]);
  useEffect(() => { if (activeTab === 'analytics') { fetchFinancialYears(); fetchAnalytics(); } }, [activeTab, fetchAnalytics, fetchFinancialYears]);
  useEffect(() => { if (activeTab === 'analytics' && selectedFY) fetchAnalytics(); }, [selectedFY]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [documentType, statusFilter, searchTerm, vendorFilter, departmentFilter, dateFrom, dateTo]);

  const totalPages = Math.ceil(total / pageSize);

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDocumentType('');
    setStatusFilter('');
    setVendorFilter('');
    setDepartmentFilter('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const hasActiveFilters = documentType || statusFilter || searchTerm || vendorFilter || departmentFilter || dateFrom || dateTo;

  const handleRowClick = (doc) => {
    const config = DOC_TYPE_CONFIG[doc.DocumentType];
    if (!config) return;
    if (doc.DocumentType === 'Invoice') navigate(`/invoices/${doc.DocumentID}`);
    else if (doc.DocumentType === 'Expense') navigate(`/expenses/${doc.DocumentID}`);
    else if (doc.DocumentType === 'Accrual') navigate('/accruals');
    else if (doc.DocumentType === 'Subscription') navigate('/subscriptions');
  };

  // ── Summary cards ──
  const summaryCards = [
    { type: 'Invoice', label: 'Invoices', icon: FileText, color: 'blue' },
    { type: 'Expense', label: 'Expenses', icon: Receipt, color: 'purple' },
    { type: 'Accrual', label: 'Accruals', icon: BookOpen, color: 'amber' },
    { type: 'Subscription', label: 'Subscriptions', icon: RefreshCw, color: 'green' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FolderOpen className="w-7 h-7 text-indigo-600" />
            All Documents
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Unified view of invoices, expenses, accruals and subscriptions
          </p>
        </div>
        {activeTab === 'documents' && (
          <div className="text-right">
            <div className="text-sm text-gray-500">
              {total.toLocaleString()} document{total !== 1 ? 's' : ''} found
            </div>
            {total > 0 && (
              <div className="text-sm font-semibold text-gray-700">
                Total: {formatCurrency(filteredAmount)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('documents')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
            activeTab === 'documents'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <FileText size={16} /> Documents
        </button>
        <button
          onClick={() => setActiveTab('analytics')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
            activeTab === 'analytics'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <BarChart3 size={16} /> Analytics
        </button>
      </div>

      {/* ═══ DOCUMENTS TAB ═══ */}
      {activeTab === 'documents' && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            {summaryCards.map(({ type, label, icon: Icon, color }) => {
              const s = stats[type] || { count: 0, totalAmount: 0 };
              const isSelected = documentType === type;
              return (
                <button
                  key={type}
                  onClick={() => setDocumentType(isSelected ? '' : type)}
                  className={`bg-white border rounded-xl p-4 text-left transition-all hover:shadow-md ${
                    isSelected ? `ring-2 ring-${color}-500 border-${color}-300` : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <Icon size={20} className={`text-${color}-500`} />
                    <span className="text-2xl font-bold text-gray-900">{s.count}</span>
                  </div>
                  <p className="text-sm text-gray-500">{label}</p>
                  <p className={`text-sm font-semibold text-${color}-600 mt-0.5`}>
                    {formatCurrency(s.totalAmount)}
                  </p>
                </button>
              );
            })}
          </div>

      {/* Search & Filters */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 space-y-3">
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by reference, vendor, description..."
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            />
          </div>

          {/* Document Type */}
          <select
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 min-w-[150px]"
          >
            <option value="">All Types</option>
            <option value="Invoice">Invoices</option>
            <option value="Expense">Expenses</option>
            <option value="Accrual">Accruals</option>
            <option value="Subscription">Subscriptions</option>
          </select>

          {/* Status */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 min-w-[140px]"
          >
            <option value="">All Statuses</option>
            {statuses.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>

          {/* Toggle advanced filters */}
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={`flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm transition-colors ${
              showAdvancedFilters ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Filter size={14} /> Filters
          </button>

          {/* Clear filters */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg border border-red-200 transition-colors"
            >
              <X size={14} /> Clear
            </button>
          )}
        </div>

        {/* Advanced filters */}
        {showAdvancedFilters && (
          <div className="grid grid-cols-4 gap-3 pt-3 border-t border-gray-100">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Vendor / Employee</label>
              <input
                type="text"
                value={vendorFilter}
                onChange={(e) => setVendorFilter(e.target.value)}
                placeholder="Filter by vendor..."
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Department</label>
              <input
                type="text"
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                placeholder="Filter by department..."
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Date From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Date To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-12 text-center text-gray-400">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-3"></div>
            Loading documents...
          </div>
        ) : documents.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <FolderOpen size={40} className="mx-auto mb-3 opacity-50" />
            <p className="font-medium">No documents found</p>
            <p className="text-sm mt-1">
              {hasActiveFilters ? 'Try adjusting your filters' : 'No documents have been created yet'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <SortHeader label="Type" col="type" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <SortHeader label="Reference" col="reference" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <SortHeader label="Vendor / Employee" col="vendor" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Description</th>
                    <SortHeader label="Amount" col="amount" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" />
                    <SortHeader label="Date" col="date" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <SortHeader label="Status" col="status" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Department</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-600">
                      <Paperclip size={14} className="inline" />
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {documents.map((doc, idx) => (
                    <DocumentRow key={`${doc.DocumentType}-${doc.DocumentID}-${idx}`} doc={doc} onClick={() => handleRowClick(doc)} />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="grid grid-cols-3 items-center gap-3 px-4 py-3 border-t border-gray-200 bg-gray-50 text-sm text-gray-600">
                <div className="flex items-center gap-4">
                  <p className="text-sm text-gray-500">
                    Showing {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, total)} of {total.toLocaleString()}
                  </p>
                  <p className="text-sm font-semibold text-gray-700">
                    Total: {formatCurrency(filteredAmount)}
                  </p>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-1.5 border border-gray-300 rounded-lg bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`w-8 h-8 text-sm rounded-lg transition-colors ${
                          page === pageNum
                            ? 'bg-indigo-600 text-white border border-indigo-600'
                            : 'border border-gray-300 bg-white hover:bg-gray-100 text-gray-600'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-1.5 border border-gray-300 rounded-lg bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
                <div />
              </div>
            )}
          </>
        )}
      </div>
        </>
      )}

      {/* ═══ ANALYTICS TAB ═══ */}
      {activeTab === 'analytics' && (
        <AnalyticsView
          data={analyticsData}
          loading={analyticsLoading}
          financialYears={financialYears}
          selectedFY={selectedFY}
          onFYChange={setSelectedFY}
        />
      )}
    </div>
  );
}




// ═══════════════════════════════════════════════════════════════════════════
// SORTABLE HEADER
// ═══════════════════════════════════════════════════════════════════════════
function SortHeader({ label, col, sortBy, sortDir, onSort, align = 'left' }) {
  const active = sortBy === col;
  return (
    <th
      onClick={() => onSort(col)}
      className={`px-4 py-3 font-medium text-gray-600 cursor-pointer hover:text-gray-900 select-none transition-colors ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active ? (
          sortDir === 'asc' ? <ArrowUp size={13} className="text-indigo-600" /> : <ArrowDown size={13} className="text-indigo-600" />
        ) : (
          <ArrowUpDown size={13} className="text-gray-300" />
        )}
      </span>
    </th>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// DOCUMENT ROW
// ═══════════════════════════════════════════════════════════════════════════
function DocumentRow({ doc, onClick }) {
  const typeConfig = DOC_TYPE_CONFIG[doc.DocumentType] || DOC_TYPE_CONFIG.Invoice;
  const TypeIcon = typeConfig.icon;
  const statusColor = STATUS_COLORS[doc.Status] || 'bg-gray-100 text-gray-600 border-gray-200';

  return (
    <tr
      onClick={onClick}
      className="hover:bg-gray-50 transition-colors cursor-pointer"
    >
      {/* Type badge */}
      <td className="px-4 py-3">
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${typeConfig.color}`}>
          <TypeIcon size={12} />
          {doc.DocumentType}
        </span>
      </td>

      {/* Reference */}
      <td className="px-4 py-3">
        <span className="font-medium text-gray-900">{doc.Reference || '—'}</span>
      </td>

      {/* Vendor */}
      <td className="px-4 py-3">
        <span className="text-gray-700">{doc.VendorName || '—'}</span>
      </td>

      {/* Description */}
      <td className="px-4 py-3">
        <span className="text-gray-600 max-w-[220px] truncate block" title={doc.Description}>
          {doc.Description || '—'}
        </span>
      </td>

      {/* Amount */}
      <td className="px-4 py-3 text-right font-mono font-medium text-gray-900">
        {formatCurrency(doc.Amount, doc.Currency)}
      </td>

      {/* Date */}
      <td className="px-4 py-3 text-gray-600">
        {formatDate(doc.DocumentDate)}
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusColor}`}>
          {doc.Status}
        </span>
      </td>

      {/* Department */}
      <td className="px-4 py-3">
        {doc.DepartmentName ? (
          <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
            {doc.DepartmentName}
          </span>
        ) : (
          <span className="text-gray-300">—</span>
        )}
      </td>

      {/* Attachments */}
      <td className="px-4 py-3 text-center">
        {doc.AttachmentCount > 0 ? (
          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
            <Paperclip size={12} /> {doc.AttachmentCount}
          </span>
        ) : (
          <span className="text-gray-300">—</span>
        )}
      </td>
    </tr>
  );
}
